import React from 'react';
import {
    Container,
    Typography,
    Box,
    Grid,
    Card,
    CardContent,
    Button,
    Stack,
    Divider,
    Paper,
    Avatar,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useSEO from '../hooks/useSEO';
import ComputerIcon from '@mui/icons-material/Computer';
import LanguageIcon from '@mui/icons-material/Language';
import SecurityIcon from '@mui/icons-material/Security';
import ShareIcon from '@mui/icons-material/Share';
import CloudQueueIcon from '@mui/icons-material/CloudQueue';
import GroupsIcon from '@mui/icons-material/Groups';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

const HireUsPage: React.FC = () => {
    const navigate = useNavigate();
    useSEO({
        title: 'دریافت مشاوره IT از چیستا',
        description: 'تیم فنی چیستا را برای طراحی وب‌سایت، اپلیکیشن و خدمات فناوری اطلاعات استخدام کنید.',
        canonical: '/hire-us',
    });

    const services = [
        {
            title: 'طراحی و توسعه وب‌سایت',
            titleEn: 'Website Design & Development',
            desc: 'طراحی وب‌سایت‌های مدرن، واکنش‌گرا و بهینه شده برای موتورهای جستجو با آخرین تکنولوژی‌های روز دنیا.',
            icon: <LanguageIcon sx={{ fontSize: 40 }} />,
            color: '#2854a1',
            bg: '#eff6ff'
        },
        {
            title: 'پشتیبانی و خدمات IT',
            titleEn: 'IT Support & Services',
            desc: 'ارائه راهکارهای جامع فناوری اطلاعات و پشتیبانی فنی مداوم برای تضمین عملکرد بی‌نقص سیستم‌های شما.',
            icon: <ComputerIcon sx={{ fontSize: 40 }} />,
            color: '#0891b2',
            bg: '#ecfeff'
        },
        {
            title: 'امنیت و تست نفوذپذیری',
            titleEn: 'Vulnerability & Security',
            desc: 'بررسی دقیق لایه‌های امنیتی، شناسایی نقاط ضعف و انجام تست‌های نفوذپذیری برای محافظت از داده‌های شما.',
            icon: <SecurityIcon sx={{ fontSize: 40 }} />,
            color: '#dc2626',
            bg: '#fef2f2'
        },
        {
            title: 'مدیریت شبکه‌های اجتماعی',
            titleEn: 'Social Media Maintenance',
            desc: 'مدیریت حرفه‌ای اینستاگرام و فیس‌بوک، تولید محتوا و تعامل با مخاطبان برای رشد برند شما.',
            icon: <ShareIcon sx={{ fontSize: 40 }} />,
            color: '#7c3aed',
            bg: '#f5f3ff'
        },
        {
            title: 'سرورهای ابری (Cloud)',
            titleEn: 'Cloud Servers (99.99% SLA)',
            desc: 'ارائه سرویس‌های میزبانی ابری با پایداری ۹۹.۹۹٪ و سرعت فوق‌العاده برای میزبانی وب‌سایت‌ها و اپلیکیشن‌ها.',
            icon: <CloudQueueIcon sx={{ fontSize: 40 }} />,
            color: '#059669',
            bg: '#f0fdf4'
        },
        {
            title: 'تیم متخصصین حرفه‌ای',
            titleEn: 'Professional Team',
            desc: 'بهره‌مندی از دانش و تجربه تیمی از متخصصین ارشد در حوزه‌های مختلف تکنولوژی و بازاریابی دیجیتال.',
            icon: <GroupsIcon sx={{ fontSize: 40 }} />,
            color: '#f59e0b',
            bg: '#fffbeb'
        }
    ];

    return (
        <Box sx={{ bgcolor: '#f8fafc', pb: 10 }}>
            {/* Hero Section */}
            <Box
                sx={{
                    bgcolor: '#1e293b',
                    color: 'white',
                    py: { xs: 8, md: 15 },
                    textAlign: 'center',
                    position: 'relative',
                    overflow: 'hidden',
                    background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)'
                }}
            >
                <Container maxWidth="md">
                    <Typography
                        variant="h1"
                        fontWeight={900}
                        sx={{
                            mb: 3,
                            fontSize: { xs: '1.8rem', md: '2.5rem' },
                            textShadow: '0 4px 12px rgba(0,0,0,0.3)'
                        }}
                    >
                        دریافت مشاوره IT از چیستا
                    </Typography>
                    <Typography
                        variant="h6"
                        sx={{
                            opacity: 0.9,
                            direction: 'rtl',
                            lineHeight: 1.8,
                            fontWeight: 400,
                            maxWidth: '750px',
                            mx: 'auto',
                            mb: 6
                        }}
                    >
                        ما فراتر از یک دایرکتوری هستیم. تیم فنی چیستا آماده ارائه راهکارهای نوین تکنولوژی برای کسب و کار شماست.
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/free-consultation')}
                        sx={{
                            bgcolor: '#2854a1',
                            borderRadius: 3,
                            px: 6,
                            py: 2,
                            fontSize: '1.1rem',
                            fontWeight: 800,
                            boxShadow: '0 10px 15px -3px rgba(40, 84, 161, 0.4)',
                            '&:hover': { bgcolor: '#1e3d7a' }
                        }}
                    >
                        {'\u2067دریافت مشاوره IT\u2069'}
                    </Button>
                </Container>
                {/* Abstract Shapes */}
                <Box sx={{ position: 'absolute', top: -100, right: -100, width: 400, height: 400, borderRadius: '50%', background: 'radial-gradient(circle, rgba(40, 84, 161, 0.15) 0%, transparent 70%)' }} />
                <Box sx={{ position: 'absolute', bottom: -150, left: -150, width: 500, height: 500, borderRadius: '50%', background: 'radial-gradient(circle, rgba(255,255,255,0.05) 0%, transparent 70%)' }} />
            </Box>

            <Container sx={{ mt: -8, position: 'relative', zIndex: 2 }}>
                <Grid container spacing={4}>
                    {services.map((service, index) => (
                        <Grid size={{ xs: 12, md: 4 }} key={index}>
                            <Card
                                sx={{
                                    height: '100%',
                                    borderRadius: 5,
                                    boxShadow: '0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -2px rgba(0,0,0,0.05)',
                                    border: '1px solid #e2e8f0',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        transform: 'translateY(-12px)',
                                        boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)',
                                        borderColor: service.color
                                    }
                                }}
                            >
                                <CardContent sx={{ p: 4 }}>
                                    <Avatar
                                        sx={{
                                            width: 70,
                                            height: 70,
                                            mb: 3,
                                            bgcolor: service.bg,
                                            color: service.color
                                        }}
                                    >
                                        {service.icon}
                                    </Avatar>
                                    <Typography variant="h5" fontWeight={800} sx={{ mb: 1, color: '#1e293b', textAlign: 'right', direction: 'rtl' }}>
                                        {service.title}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: service.color, fontWeight: 700, mb: 3, display: 'block', letterSpacing: 0.5 }}>
                                        {service.titleEn}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b', lineHeight: 1.8, direction: 'rtl', textAlign: 'right' }}>
                                        {service.desc}
                                    </Typography>
                                </CardContent>
                            </Card>
                        </Grid>
                    ))}
                </Grid>

                {/* Detailed Features Section */}
                <Grid container spacing={6} sx={{ mt: 10, alignItems: 'center' }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ direction: 'rtl' }}>
                            <Typography variant="h3" fontWeight={800} sx={{ mb: 4, color: '#1e293b' }}>
                                چرا تیم فنی چیستا؟
                            </Typography>
                            <List>
                                {[
                                    'تعهد به کیفیت و استانداردهای جهانی وب',
                                    'استفاده از سرورهای اختصاصی با پایداری ۹۹.۹۹٪',
                                    'پشتیبانی فنی ۲۴ ساعته در ۷ روز هفته',
                                    'امنیت تضمین شده و تست‌های دوره‌ای آسیب‌پذیری',
                                    'تیمی متشکل از طراحان و توسعه‌دهندگان ارشد',
                                    'ارائه راهکارهای سفارشی متناسب با بودجه شما'
                                ].map((text, idx) => (
                                    <ListItem key={idx} sx={{ px: 0, textAlign: 'right' }}>
                                        <ListItemIcon sx={{ minWidth: 40, justifyContent: 'flex-start' }}>
                                            <CheckCircleIcon sx={{ color: '#2854a1' }} />
                                        </ListItemIcon>
                                        <ListItemText 
                                            primary={text} 
                                            primaryTypographyProps={{ 
                                                fontWeight: 600, 
                                                color: '#334155',
                                                textAlign: 'right'
                                            }}
                                        />
                                    </ListItem>
                                ))}
                            </List>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1,
                                bgcolor: 'white',
                                borderRadius: 6,
                                border: '1px solid #e2e8f0',
                                boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)'
                            }}
                        >
                            <Box
                                component="img"
                                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?auto=format&fit=crop&w=800&q=80"
                                alt="Professional Technical Team"
                                sx={{ width: '100%', borderRadius: 5, display: 'block' }}
                            />
                        </Paper>
                    </Grid>
                </Grid>

                {/* Final CTA */}
                <Box
                    sx={{
                        mt: 12,
                        p: { xs: 5, md: 8 },
                        borderRadius: 8,
                        bgcolor: '#2854a1',
                        color: 'white',
                        textAlign: 'center',
                        position: 'relative',
                        overflow: 'hidden'
                    }}
                >
                    <Typography variant="h3" fontWeight={900} sx={{ mb: 3 }}>
                        پروژه خود را با ما شروع کنید
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 2, opacity: 0.9, direction: 'rtl', maxWidth: '600px', mx: 'auto' }}>
                        برای دریافت برآورد هزینه و زمان پروژه، کافیست از طریق فرم تماس با ما در ارتباط باشید.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 6, fontWeight: 700, color: '#fbbf24', direction: 'rtl' }}>
                        هزینه انجام خدمات: $80 در ساعت (Hourly Rate: $80)
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={() => navigate('/free-consultation')}
                        sx={{
                            bgcolor: 'white',
                            color: '#2854a1',
                            borderRadius: 3,
                            px: 8,
                            py: 2,
                            fontWeight: 800,
                            fontSize: '1.1rem',
                            '&:hover': { bgcolor: '#f1f5f9' }
                        }}
                    >
                        {'\u2067دریافت مشاوره IT\u2069'}
                    </Button>
                </Box>
            </Container>
        </Box>
    );
};

export default HireUsPage;
